import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';

import { useMutation } from '@tanstack/react-query';
import { deletedImportFCL } from '@/services/deletedImportFCL';
import { useToastNotification } from '@/hooks';
import { queryClient } from '@/providers/AppProvider';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components';
import styles from './ImportFCL.module.scss';

type ModalDetailsProps = {
  items: number[];
  disabled?: boolean;
  textButton?: string;
};

const DeleteImportFCL: FC<ModalDetailsProps> = ({
  items,
  disabled,
  textButton,
}) => {
  const { showToast } = useToastNotification();
  const { t } = useTranslation('importFCL');
  const [isDialogShown, setIsDialogShown] = useState(false);

  const count = items?.length || 0;

  const toggleDialog = () => {
    setIsDialogShown(!isDialogShown);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: deletedImportFCL,
    onSuccess: () => {
      showToast({
        severity: 'success',
        detail: t('containersDeleted', { count }),
      });
      queryClient.invalidateQueries({
        queryKey: ['getImportFCLTable'],
      });
      toggleDialog();
    },
    onError: () => {
      showToast({
        severity: 'error',
        detail: t('containersNotDeleted'),
      });
    },
  });

  const onClick = () => {
    toggleDialog();
  };

  const dialogHeader = (
    <div className="text-blue-800">
      <FontAwesomeIcon icon={faTrash} style={{ marginRight: '0.5rem' }} />
      {t('removeContainer')}
    </div>
  );

  const onDeleteButtonClick = () => {
    if (isPending) return;
    mutate({ oidLiquidacion: items });
  };

  const footerContent = (
    <div className="w-full flex justify-content-end ">
      <Button
        label={t('cancel')}
        link
        onClick={toggleDialog}
        disabled={isPending}
        style={{ color: '#170a33', width: '100px' }}
      />
      <Button
        style={{ backgroundColor: '#FF0000', color: '#FFFFFF', width: '100px' }}
        label={t('delete')}
        onClick={onDeleteButtonClick}
        loading={isPending}
      />
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || isPending}
        className={classNames(styles.secondaryActionsButtons)}
      >
        <FontAwesomeIcon
          icon={faTrash}
          className="mr-2"
          style={{ color: disabled || isPending ? '#cccccc' : undefined }}
        />
        {!!textButton && <span className="hidden md:flex">{textButton}</span>}
      </button>
      {isDialogShown && (
        <Dialog
          header={dialogHeader}
          visible={isDialogShown}
          breakpoints={{ '1200px': '95vw' }}
          onHide={toggleDialog}
          footer={footerContent}
        >
          <div className="px-6 pb-6">
            <p>{t('removeContainers', { count })}</p>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default DeleteImportFCL;
